import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaChartBar,
  FaChevronDown,
  FaCrown,
  FaFileContract,
  FaGlobe,
  FaHome,
  FaKey,
  FaSearch,
  FaSignOutAlt,
  FaTimes,
  FaUser,
  FaUserAlt,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";
import {
  Bell,
  Menu,
  MessageCircle,
  PhoneCall,
  Plus,
  RefreshCw,
  Search,
  Share2,
  UserRoundPlus,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { themeClasses } from "./colors";
import { getRequest, putRequest } from "../helpers/api.helper";
import {
  LANG_LABELS,
  SupportedLanguage,
  useLanguage,
} from "../context/LanguageContext";
import { RootState } from "../store";
import { removeSessionItem } from "../helpers/function.helper";
import { clearBureau, setBureau } from "../store/bureau.slice";
import { clearDashboard } from "../store/dashboard.slice";
import { UPLOAD_URL } from "../utils/constants";
import { getGenderPlaceholder } from "../helpers/matches.helper";
import { hasActiveMembership } from "../utils/bureau";
import BiodataUploadModal from "../pages/UserCreation/BiodataUploadModal";

type NotificationRecord = {
  _id: string;
  title?: string;
  message?: string;
  type?: string;
  isRead?: boolean;
  createdAt?: string;
  data?: {
    fullName?: string;
    caste?: string;
    annualIncome?: string;
    citizenship?: string;
    district?: string;
    gender?: string;
    image?: string;
    profileCreatedAt?: string;
    martialId?: string;
    maritalId?: string;
    userId?: string;
    isOtherBureau?: boolean;
    url?: string;
  };
};

const NEW_PROFILE_NOTIFICATION_TITLE =
  "New profile created by Matrimony studio";

const cleanNotificationValue = (value?: string | null) => {
  if (!value) return "";
  const text = value.trim();
  if (!text) return "";
  const normalized = text.toLowerCase();
  return normalized === "null" || normalized === "undefined" ? "" : text;
};

const defaultAvatarUrl =
  "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png";

const formatNotificationTime = (value?: string) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNotificationHeading = (notification: NotificationRecord) =>
  notification.type === "NEW_PROFILE_ADDED"
    ? NEW_PROFILE_NOTIFICATION_TITLE
    : notification.title || "Notification";

const getNotificationSummary = (notification: NotificationRecord) => {
  const parts = [
    cleanNotificationValue(notification.data?.fullName),
    cleanNotificationValue(notification.data?.caste),
    cleanNotificationValue(notification.data?.annualIncome),
    cleanNotificationValue(notification.data?.citizenship),
    cleanNotificationValue(notification.data?.district),
  ].filter(Boolean);

  return parts.join(" | ") || notification.message || "";
};

const getNotificationTargetUrl = (notification: NotificationRecord) => {
  if (notification.data?.url) {
    return notification.data.url;
  }

  const profileId =
    notification.data?.martialId ||
    notification.data?.maritalId ||
    notification.data?.userId;

  return profileId
    ? `/admin/profile/detail/${profileId}`
    : "/admin/my-notifications";
};

const getGreeting = () => {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "Good Morning";
  }

  if (hour < 17) {
    return "Good Afternoon";
  }

  return "Good Evening";
};

const parseCountValue = (value: string | number | undefined | null) => {
  if (value === undefined || value === null) {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const numeric = Number(
    value
      .toString()
      .trim()
      .replace(/[^\d.-]/g, ""),
  );
  return Number.isNaN(numeric) ? 0 : numeric;
};

const getMobilePageTitle = (pathname: string) => {
  if (pathname === "/admin/dashboard") return "Dashboard";
  if (pathname === "/admin/search") return "Search";
  if (pathname.startsWith("/admin/search/mobile")) return "Search by Mobile";
  if (pathname.startsWith("/admin/profile/detail/")) return "Profile Details";
  if (pathname.startsWith("/admin/profile/manage/")) return "Manage Profile";
  if (pathname.startsWith("/admin/profiles/callback")) return "Call Back Profiles";
  if (pathname.startsWith("/admin/profiles/not-responding")) {
    return "Not Responding Profiles";
  }
  if (pathname.startsWith("/admin/profiles/")) return "Profiles";
  if (pathname.startsWith("/admin/password/change")) return "Change Password";
  if (pathname.startsWith("/admin/settings/caste-preferences")) return "Edit Caste Preference";
  if (pathname.startsWith("/admin/settings/privacy")) return "Privacy";
  if (pathname.startsWith("/admin/how-to-use")) return "How To Use";
  if (pathname.startsWith("/admin/share")) return "Share";
  if (pathname.startsWith("/admin/website/edit")) return "Edit Website";
  if (pathname.startsWith("/admin/website/terms")) return "Terms & Conditions";
  if (pathname.startsWith("/admin/website/links")) return "Custom Links";
  if (pathname.startsWith("/admin/website/images")) return "Slider Images";
  if (pathname.startsWith("/admin/website/locations")) return "Locations";
  if (pathname.startsWith("/admin/website/stories")) return "Success Stories";
  if (pathname.startsWith("/admin/website/testimonials")) return "Testimonials";
  if (pathname.startsWith("/admin/website/packages")) return "Packages";
  if (pathname.startsWith("/admin/website/services")) return "Services";
  if (pathname.startsWith("/admin/website/logo")) return "Navbar Logo";
  if (pathname.startsWith("/admin/website/newDetails")) return "New Details";
  if (pathname.startsWith("/admin/website/promotion")) return "Promotion Banners";
  if (pathname.startsWith("/admin/website/social")) return "Social Media";
  if (pathname.startsWith("/admin/community/guidelines")) {
    return "Community Guidelines";
  }
  if (pathname.startsWith("/admin/my-membership")) return "My Membership";
  if (pathname.startsWith("/admin/my-notifications")) return "Notifications";
  if (pathname.startsWith("/admin/user/profile/basic/")) return "Add Profile";
  if (pathname.startsWith("/admin/user/profile/")) return "Add Profile";
  if (pathname.startsWith("/admin/edit-profile/")) return "Edit Profile";
  if (pathname.startsWith("/admin/step_")) return "Edit Profile";

  return "";
};

type GnavbarProps = {
  onRefreshDashboard?: () => void;
  dashboardRefreshing?: boolean;
};

const Gnavbar: React.FC<GnavbarProps> = ({
  onRefreshDashboard,
  dashboardRefreshing = false,
}) => {
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { language, setLanguage, t } = useLanguage();

  const [isAddProfileModalOpen, setIsAddProfileModalOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] =
    useState(false);

  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [leadCounts, setLeadCounts] = useState({
    whatsappClicks: 0,
    callClicks: 0,
  });

  const [expandedOption, setExpandedOption] = useState<string | null>(null);
  const [isMediatorProfile, setIsMediatorProfile] = useState(false);
  const [activeProfileType, setActiveProfileType] = useState<string | null>(
    null,
  );

  const [addMode, setAddMode] = useState<"ai" | "manual" | null>(null);
  const [searchMobile, setSearchMobile] = useState("");
  const [searchMobileError, setSearchMobileError] = useState("");
  const [searchMobileLoading, setSearchMobileLoading] = useState(false);

  const languageMenuRef = useRef<HTMLDivElement | null>(null);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  const isPaidAccount = hasActiveMembership(bureauInfo?.paymentStatus);
  const isDashboardHome = location.pathname === "/admin/dashboard";
  const mobilePageTitle = getMobilePageTitle(location.pathname);
  const mobileNavShellClassName = isDashboardHome
    ? "fixed inset-x-0 top-0 z-50 overflow-hidden bg-[#C062D4] md:sticky md:overflow-visible md:border-b md:bg-white md:shadow-none"
    : "sticky top-0 z-50 border-b border-white/70 bg-[linear-gradient(180deg,rgba(255,248,240,0.98)_0%,rgba(255,255,255,0.94)_58%,rgba(255,255,255,0.92)_100%)] shadow-[0_10px_30px_rgba(148,163,184,0.14)] backdrop-blur-md md:border-b md:bg-white md:shadow-none";
  const mobileGreetingTextClassName = isDashboardHome
    ? "text-[12px] font-medium text-white/75"
    : "text-[12px] font-medium text-slate-500";
  const mobileTitleTextClassName = isDashboardHome
    ? "truncate text-[18px] font-medium leading-tight text-white"
    : "truncate text-[18px] font-semibold leading-tight text-slate-900";
  const mobileAvatarClassName = isDashboardHome
    ? "h-10 w-10 shrink-0 rounded-full border-2 border-white/80 object-cover shadow-[0_8px_18px_rgba(15,23,42,0.18)]"
    : "h-10 w-10 shrink-0 rounded-full border-2 border-white object-cover shadow-[0_8px_18px_rgba(15,23,42,0.12)]";
  const mobileFallbackAvatarClassName = isDashboardHome
    ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-white/80 bg-white/15 text-lg font-bold text-white shadow-[0_8px_18px_rgba(15,23,42,0.18)]"
    : "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-fuchsia-500 to-violet-600 text-lg font-bold text-white shadow-[0_8px_18px_rgba(15,23,42,0.12)]";

  const avatarSrc = bureauInfo?.image
    ? `${UPLOAD_URL}/bureaus/profile_pic/${bureauInfo.image}`
    : defaultAvatarUrl;

  const handleMobileBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/admin/dashboard", { replace: true });
  };

  const openAddProfileModal = () => {
    setExpandedOption(null);
    setAddMode(null);
    setIsMediatorProfile(false);
    setIsAddProfileModalOpen(true);
  };

  const closeAddProfileModal = () => {
    setIsAddProfileModalOpen(false);
    setExpandedOption(null);
    setAddMode(null);
    setIsMediatorProfile(false);
    setActiveProfileType(null);
    setSearchMobileError("");
  };

  const handleSearchMobile = () => {
    const cleaned = searchMobile.replace(/\D/g, "").slice(0, 10);

    if (!cleaned) {
      setSearchMobileError("Please enter mobile number.");
      return;
    }

    setSearchMobileError("");
    setSearchMobileLoading(true);

    getRequest(`/user/profile/by-mobile/${cleaned}`)
      .then((response) => {
        const list = Array.isArray(response?.data) ? response.data : [];

        if (response?.success && list.length > 0) {
          closeAddProfileModal();

          navigate("/admin/search/mobile", {
            state: {
              mobile: cleaned,
            },
          });

          return;
        }

        setSearchMobileError(
          response?.isDeleted
            ? "This profile is deleted"
            : "No profile found with that mobile number.",
        );
      })
      .catch(() => {
        setSearchMobileError("Something went wrong. Please try again.");
      })
      .finally(() => {
        setSearchMobileLoading(false);
      });
  };

  const fetchNotifications = async () => {
    try {
      if (!bureauInfo) {
        return;
      }

      const response = await getRequest("/firebase");

      if (response.success) {
        const items = Array.isArray(response.data) ? response.data : [];

        setNotifications(items as NotificationRecord[]);

        setNotificationCount(
          items.filter((item: NotificationRecord) => !item.isRead).length,
        );
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchLeadCounts = async () => {
    try {
      const response = await getRequest("/bureau/dashboard");
      const payload =
        response?.data?.dashboard ?? response?.data?.data ?? response?.data;

      if (response?.success && payload) {
        setLeadCounts({
          whatsappClicks: parseCountValue(
            payload?.meta?.whatsappClicks ?? payload?.whatsappClicks,
          ),
          callClicks: parseCountValue(
            payload?.meta?.callClicks ?? payload?.callClicks,
          ),
        });
      }
    } catch (error) {
      console.error("Error fetching lead counts:", error);
    }
  };

  const userInfo = async () => {
    try {
      const response = await getRequest("/bureau/me");

      if (response.success) {
        dispatch(
          setBureau({
            ...response.data.profile,
          }),
        );
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  useEffect(() => {
    userInfo();
    fetchNotifications();
    fetchLeadCounts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(target)
      ) {
        setLanguageMenuOpen(false);
      }

      if (profileMenuRef.current && !profileMenuRef.current.contains(target)) {
        setProfileMenuOpen(false);
      }
    };

    if (languageMenuOpen || profileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [languageMenuOpen, profileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileOpen((previous) => !previous);
    setLanguageMenuOpen(false);
    setProfileMenuOpen(false);
    setShowNotificationDropdown(false);
  };

  const handleLanguageSelect = (selectedLanguage: SupportedLanguage) => {
    setLanguage(selectedLanguage);
    setLanguageMenuOpen(false);
  };

  const closeProfileMenu = () => {
    setProfileMenuOpen(false);
  };

  const redirectToUpgradePlans = () => {
    closeProfileMenu();
    setLanguageMenuOpen(false);
    setIsMobileOpen(false);
    setShowNotificationDropdown(false);
    navigate("/admin/membership/plans");
  };

  const handleDropdownNavigate = (path: string, requiresPaid = false) => {
    if (requiresPaid && !isPaidAccount) {
      redirectToUpgradePlans();
      return;
    }

    navigate(path);
    closeProfileMenu();
    setIsMobileOpen(false);
  };

  const handleMobileNavigate = (path: string, requiresPaid = false) => {
    if (requiresPaid && !isPaidAccount) {
      redirectToUpgradePlans();
      return;
    }

    navigate(path);
    setIsMobileOpen(false);
    setShowNotificationDropdown(false);
  };

  const handleNotificationClick = async (notification: NotificationRecord) => {
    try {
      await putRequest(`/firebase/${notification._id}/read`, {});
    } catch (error) {
      console.error("Error updating notification status:", error);
    }

    const targetUrl = getNotificationTargetUrl(notification);

    setNotifications((previous) =>
      previous.filter((item) => item._id !== notification._id),
    );

    setNotificationCount((previous) => Math.max(previous - 1, 0));

    setShowNotificationDropdown(false);
    navigate(targetUrl, { state: { openedFromNotification: true } });
  };

  const handleViewAllNotifications = () => {
    setShowNotificationDropdown(false);
    navigate("/admin/my-notifications");
  };

  const handleProfileMenuToggle = () => {
    setProfileMenuOpen((previous) => !previous);
    setLanguageMenuOpen(false);
  };

  const handleLogout = () => {
    closeProfileMenu();
    setLanguageMenuOpen(false);
    removeSessionItem("sessionToken");
    removeSessionItem("bureauData");
    dispatch(clearBureau());
    dispatch(clearDashboard());
    navigate("/admin/login");
  };

  const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
      isActive
        ? "bg-purple-50 text-purple-600"
        : "text-gray-700 hover:bg-gray-100"
    }`;

  const renderNotificationBody = () => (
    <>
      <div className="border-b p-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {t("navbar.notificationsTitle")}
        </h3>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {notifications.length > 0 ? (
          notifications.map((notification) => {
            const summary = getNotificationSummary(notification);
            const formattedTime = formatNotificationTime(
              notification.data?.profileCreatedAt || notification.createdAt,
            );

            return (
              <button
                key={notification._id}
                type="button"
                onClick={() => handleNotificationClick(notification)}
                className="block w-full cursor-pointer border-b p-3 text-left hover:bg-gray-50"
              >
                <div className="flex items-start gap-3">
                  <img
                    src={
                      notification.data?.image
                        ? `${UPLOAD_URL}/users/profile_pic/${notification.data.image}`
                        : getGenderPlaceholder(notification.data?.gender)
                    }
                    onError={(event) => {
                      event.currentTarget.src = getGenderPlaceholder(
                        notification.data?.gender,
                      );
                    }}
                    alt={notification.data?.fullName || "profile"}
                    className="h-10 w-10 rounded-full border border-gray-200 object-cover"
                  />

                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800">
                      <span className="font-semibold">
                        {getNotificationHeading(notification)}
                      </span>
                    </p>

                    {summary ? (
                      <p className="mt-1 text-xs text-gray-600">{summary}</p>
                    ) : null}

                    {formattedTime ? (
                      <p className="mt-1 text-xs text-gray-500">
                        {formattedTime}
                      </p>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })
        ) : (
          <div className="p-4 text-center text-gray-500">
            {t("navbar.notificationsEmpty")}
          </div>
        )}
      </div>

      <div className="border-t p-3">
        <button
          type="button"
          onClick={handleViewAllNotifications}
          className="text-sm text-purple-600 hover:underline"
        >
          {t("navbar.notificationsViewAll")}
        </button>
      </div>
    </>
  );

  if (!bureauInfo) {
    return (
      <nav className="safe-area-top sticky top-0 z-50 border-b bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-end sm:h-16">
            <div className="relative hidden md:block" ref={languageMenuRef}>
              <button
                type="button"
                onClick={() => setLanguageMenuOpen((previous) => !previous)}
                className="flex items-center space-x-2 rounded-full bg-gray-100 px-3 py-2 text-gray-700 transition hover:bg-gray-200"
              >
                <FaGlobe size={16} />

                <span className="text-xs font-semibold uppercase">
                  {LANG_LABELS[language]}
                </span>

                <FaChevronDown size={12} />
              </button>

              {languageMenuOpen && (
                <div className="absolute right-0 z-30 mt-2 w-40 rounded-lg border bg-white py-2 shadow-lg">
                  {(["en", "te"] as SupportedLanguage[]).map(
                    (languageOption) => (
                      <button
                        key={languageOption}
                        type="button"
                        onClick={() => handleLanguageSelect(languageOption)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                          language === languageOption
                            ? "font-semibold text-purple-600"
                            : "text-gray-700"
                        }`}
                      >
                        {LANG_LABELS[languageOption]}
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <div>
      <nav
        id={isDashboardHome ? "dashboard-mobile-navbar" : undefined}
        className={mobileNavShellClassName}
      >
        {/* Mobile header */}
        <div className="relative px-5 pb-4 pt-[calc(env(safe-area-inset-top)+16px)] md:hidden">
          {isDashboardHome ? (
            <>
              <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-3xl" />
              <div className="pointer-events-none absolute -left-16 bottom-0 h-36 w-36 rounded-full bg-fuchsia-300/15 blur-3xl" />
            </>
          ) : (
            <>
              <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-[radial-gradient(circle_at_top_left,rgba(251,191,36,0.12),transparent_38%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.12),transparent_34%)]" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
            </>
          )}
          <div className="relative">
            <div className="flex items-center justify-between gap-3">
              {isDashboardHome ? (
                <NavLink
                  to="/admin/dashboard"
                  className="flex min-w-0 items-center gap-3"
                >
                  {bureauInfo.image ? (
                    <img
                      src={avatarSrc}
                      alt={bureauInfo.bureauName}
                      className={mobileAvatarClassName}
                    />
                  ) : (
                    <div className={mobileFallbackAvatarClassName}>
                      {(bureauInfo.bureauName?.charAt(0) || "M").toUpperCase()}
                    </div>
                  )}

                  <div className="min-w-0">
                    <p className={mobileGreetingTextClassName}>
                      {t(getGreeting())}
                    </p>

                    <h1 className={mobileTitleTextClassName}>
                      {bureauInfo.bureauName || ""}
                    </h1>
                  </div>
                </NavLink>
              ) : (
                <div className="flex min-w-0 items-center gap-3">
                  <button
                    type="button"
                    onClick={handleMobileBack}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.12)] transition active:scale-95"
                    aria-label="Go back"
                  >
                    <FaArrowLeft size={16} />
                  </button>

                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-slate-500">
                      {bureauInfo.bureauName || ""}
                    </p>

                    <h1 className="truncate text-[18px] font-semibold leading-tight text-slate-900">
                      {mobilePageTitle}
                    </h1>
                  </div>
                </div>
              )}

              <div className="flex shrink-0 items-center gap-3">
                {isDashboardHome && onRefreshDashboard ? (
                  <button
                    type="button"
                    onClick={onRefreshDashboard}
                    disabled={dashboardRefreshing}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.14)] transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label={t("Refresh Dashboard")}
                    title={t("Refresh Dashboard")}
                  >
                    <RefreshCw
                      size={16}
                      strokeWidth={2}
                      className={dashboardRefreshing ? "animate-spin" : ""}
                    />
                  </button>
                ) : null}
                {/* <button
                  type="button"
                  onClick={() => navigate("/admin/my-notifications")}
                  className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.14)] transition active:scale-95"
                  aria-label="Notifications"
                >
                  <Bell size={16} strokeWidth={2} />

                  {notificationCount > 0 && (
                    <span className="absolute right-2 top-2 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white ring-2 ring-white">
                      {notificationCount > 9 ? "9+" : notificationCount}
                    </span>
                  )}
                </button> */}

                {isDashboardHome ? (
                  <button
                    type="button"
                    onClick={toggleMobileMenu}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.14)] transition active:scale-95"
                    aria-label={isMobileOpen ? "Close Menu" : "Open Menu"}
                  >
                    {isMobileOpen ? (
                      <FaTimes size={16} />
                    ) : (
                      <Menu size={16} strokeWidth={2} />
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop header */}
        <div className="mx-auto hidden max-w-7xl px-4 sm:px-6 md:block lg:px-8">
          <div className="flex min-h-[72px] items-center justify-between">
            <NavLink to="/admin/dashboard" className="min-w-0">
              <div className="flex items-center gap-3">
                {bureauInfo.image ? (
                  <img
                    src={avatarSrc}
                    alt={bureauInfo.bureauName}
                    className="h-12 w-12 rounded-full border-2 border-purple-100 object-cover shadow-sm"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 font-bold text-purple-700">
                    {(bureauInfo.bureauName?.charAt(0) || "M").toUpperCase()}
                  </div>
                )}

                <div className="min-w-0">
                  <h1 className="truncate bg-gradient-to-r from-purple-600 via-violet-600 to-pink-500 bg-clip-text text-xl font-semibold text-transparent">
                    {bureauInfo.bureauName || ""}
                  </h1>

                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    {!isPaidAccount && (
                      <>
                        <span className="text-xs font-semibold text-green-700">
                          {t("Free Account")}
                        </span>

                        <button
                          type="button"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            navigate("/admin/membership/plans");
                          }}
                          className="flex items-center gap-1 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 px-3 py-1 text-xs font-medium text-white transition hover:scale-105 hover:shadow-md"
                        >
                          <FaCrown size={14} />
                          {t("Upgrade")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </NavLink>

            <div className="flex items-center gap-3">
              <NavLink to="/admin/dashboard" className={navLinkClasses}>
                <FaHome size={18} />
                {t("navbar.home")}
              </NavLink>

              <NavLink to="/admin/search" className={navLinkClasses}>
                <FaSearch size={18} />
                {t("navbar.menu.search")}
              </NavLink>

              <button
                type="button"
                onClick={openAddProfileModal}
                className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-purple-600 px-4 py-2 text-white transition hover:bg-purple-700"
              >
                <Plus size={18} />

                <span className="text-sm font-medium">
                  {t("Create Profile")}
                </span>
              </button>

              <div className="relative" ref={languageMenuRef}>
                <button
                  type="button"
                  onClick={() => setLanguageMenuOpen((previous) => !previous)}
                  className="flex items-center space-x-2 rounded-full bg-gray-100 px-3 py-2 text-gray-700 transition hover:bg-gray-200"
                >
                  <FaGlobe size={16} />

                  <span className="text-xs font-semibold uppercase">
                    {LANG_LABELS[language]}
                  </span>

                  <FaChevronDown size={12} />
                </button>

                {languageMenuOpen && (
                  <div className="absolute right-0 z-30 mt-2 w-40 rounded-lg border bg-white py-2 shadow-lg">
                    {(["en", "te"] as SupportedLanguage[]).map(
                      (languageOption) => (
                        <button
                          key={languageOption}
                          type="button"
                          onClick={() => handleLanguageSelect(languageOption)}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                            language === languageOption
                              ? "font-semibold text-purple-600"
                              : "text-gray-700"
                          }`}
                        >
                          {LANG_LABELS[languageOption]}
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>

              <div className="relative" ref={profileMenuRef}>
                <button
                  type="button"
                  onClick={handleProfileMenuToggle}
                  className="flex items-center gap-2 rounded-full border border-gray-200 px-3 py-2 transition hover:bg-gray-100"
                >
                  <img
                    src={avatarSrc}
                    alt={bureauInfo.bureauName}
                    className="h-8 w-8 rounded-full object-cover"
                  />

                  <span className="text-sm font-semibold text-gray-700">
                    {t("navbar.profileMenu.myProfile")}
                  </span>

                  <FaChevronDown size={12} className="text-gray-500" />
                </button>

                {profileMenuOpen && (
                  <div className="absolute right-0 z-30 mt-2 w-60 rounded-lg border bg-white py-2 shadow-lg">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/${bureauInfo.bureauId}/${bureauInfo.bureauName?.replace(
                            /\s+/g,
                            "",
                          )}`,
                        )
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaUserAlt size={16} />
                      {t("Preview your website")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/profile/edit")
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaUserAlt size={16} />
                      {t("navbar.profileMenu.myProfile")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/my-membership")
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaUserAlt size={16} />
                      {t("My Membership")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/community/guidelines")
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaFileContract size={16} />
                      {t("Community Guidelines")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/membership/plans")
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaCrown size={16} />
                      {t("Upgrade plans")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/password/change")
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaKey size={16} />
                      {t("Change Password")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/settings/privacy", true)
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaUserShield size={16} />
                      {t("Bureau Privacy")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/how-to-use", true)
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaChartBar size={16} />
                      {t("How to Use")}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDropdownNavigate("/admin/settings/caste-preferences")}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaUsers size={16} />
                      {t("Edit Caste Preference")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/website/edit")
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FaGlobe size={16} />
                      {t("Edit Website")}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleDropdownNavigate("/admin/share", true)
                      }
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Share2 size={16} />
                      {t("Share your website")}
                    </button>

                    <div className="my-2 border-t" />

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                    >
                      <FaSignOutAlt size={16} />
                      {t("navbar.logout")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {showNotificationDropdown && (
        <div className="fixed right-4 top-16 z-50 w-80 rounded-lg border bg-white shadow-lg md:hidden">
          {renderNotificationBody()}
        </div>
      )}

      {isMobileOpen && (
        <div className="fixed inset-0 z-[10000] flex justify-end bg-gray-900/50">
          <div className="h-full w-[82%] max-w-sm overflow-y-auto bg-white p-6 pb-20 shadow-2xl">
            <div className="mb-7 flex items-center justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <img
                  src={avatarSrc}
                  alt={bureauInfo.bureauName}
                  className="h-12 w-12 rounded-full object-cover"
                />

                <div className="min-w-0">
                  <p className="truncate text-lg font-semibold text-purple-700">
                    {bureauInfo.bureauName}
                  </p>

                  <p className="text-sm text-gray-500">
                    {t("navbar.idLabel")} {bureauInfo.bureauId}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={toggleMobileMenu}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700"
                aria-label="Close Menu"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="mb-4 flex items-center gap-3 rounded-xl bg-gray-50 p-3">
              <FaGlobe size={18} className="text-gray-700" />

              <div className="flex gap-2">
                {(["en", "te"] as SupportedLanguage[]).map((languageOption) => (
                  <button
                    key={languageOption}
                    type="button"
                    onClick={() => handleLanguageSelect(languageOption)}
                    className={`rounded-full border px-3 py-1 text-sm transition ${
                      language === languageOption
                        ? "border-purple-600 bg-purple-600 text-white"
                        : "border-gray-300 bg-white text-gray-700"
                    }`}
                  >
                    {LANG_LABELS[languageOption]}
                  </button>
                ))}
              </div>
            </div>

            <section className="mb-5 rounded-[20px] bg-[linear-gradient(90deg,#FFF3F0_0%,#FDF0F5_100%)] px-4 py-3 shadow-[0_8px_24px_rgba(15,23,42,0.05)]">
              {/* Heading */}
              <div className="mb-3 flex items-center gap-2">
                <UserRoundPlus
                  size={22}
                  strokeWidth={2.2}
                  className="text-orange-500"
                />
                <span className="text-sm font-bold text-orange-600">
                  {t("Customer Leads")}
                </span>
              </div>

              {/* Counters */}
              <div className="grid grid-cols-2 divide-x divide-slate-300">
                <div className="flex items-center justify-center gap-2 py-1">
                  <PhoneCall
                    size={18}
                    strokeWidth={2}
                    className="text-slate-600"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    {t("Calls")}
                  </span>
                  <span className="text-sm font-bold text-slate-900">
                    {leadCounts.callClicks ?? 0}
                  </span>
                </div>

                <div className="flex items-center justify-center gap-2 py-1">
                  <MessageCircle
                    size={18}
                    strokeWidth={2}
                    className="text-green-500"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    {t("WhatsApp")}
                  </span>
                  <span className="text-sm font-bold text-slate-900">
                    {leadCounts.whatsappClicks ?? 0}
                  </span>
                </div>
              </div>
            </section>

            <nav className="flex flex-col divide-y divide-gray-100">
              <button
                type="button"
                onClick={() => handleMobileNavigate("/admin/profile/edit")}
                className="flex items-center gap-3 px-2 py-4 text-left text-gray-700 transition hover:text-purple-600"
              >
                <FaUserAlt size={20} />
                {t("navbar.profileMenu.myProfile")}
              </button>

              <button
                type="button"
                onClick={() => handleMobileNavigate("/admin/my-membership")}
                className="flex items-center gap-3 px-2 py-4 text-left text-gray-700 transition hover:text-purple-600"
              >
                <FaCrown size={20} />
                {t("My Membership")}
              </button>

              <button
                type="button"
                onClick={() =>
                  handleMobileNavigate("/admin/settings/privacy", true)
                }
                className="flex items-center gap-3 px-2 py-4 text-left text-gray-700 transition hover:text-purple-600"
              >
                <FaUserShield size={20} />
                {t("navbar.editPrivacy")}
              </button>

              <button
                type="button"
                onClick={() =>
                  handleMobileNavigate("/admin/community/guidelines")
                }
                className="flex items-center gap-3 px-2 py-4 text-left text-gray-700 transition hover:text-purple-600"
              >
                <FaFileContract size={20} />
                {t("navbar.mobile.community")}
              </button>

              <button
                type="button"
                onClick={() => handleMobileNavigate("/admin/password/change")}
                className="flex items-center gap-3 px-2 py-4 text-left text-gray-700 transition hover:text-purple-600"
              >
                <FaKey size={20} />
                {t("Change Password")}
              </button>

              <button
                type="button"
                onClick={() => handleMobileNavigate("/admin/settings/caste-preferences")}
                className="flex items-center gap-3 px-2 py-4 text-left text-gray-700 transition hover:text-purple-600"
              >
                <FaUsers size={20} />
                {t("Edit Caste Preference")}
              </button>

              <button
                type="button"
                onClick={() => handleMobileNavigate("/admin/website/edit")}
                className="flex items-center gap-3 px-2 py-4 text-left text-gray-700 transition hover:text-purple-600"
              >
                <FaGlobe size={20} />
                {t("navbar.mobile.editWebsite")}
              </button>

              <button
                type="button"
                onClick={() => handleMobileNavigate("/admin/how-to-use", true)}
                className="flex items-center gap-3 px-2 py-4 text-left text-gray-700 transition hover:text-purple-600"
              >
                <FaChartBar size={20} />
                {t("navbar.mobile.howToUse")}
              </button>
            </nav>

            <button
              type="button"
              onClick={handleLogout}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-red-600 px-4 py-3 text-white transition hover:bg-red-500"
            >
              <FaSignOutAlt size={20} />
              <span className="text-sm font-semibold">
                {t("navbar.mobile.logout")}
              </span>
            </button>
          </div>
        </div>
      )}

      {isAddProfileModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-gray-900/50 p-4">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                <FaUser className="h-6 w-6 text-blue-600" />
              </div>

              <h3 className="mb-4 text-lg font-medium text-gray-900">
                {t("bottomNav.modal.title")}
              </h3>

              <p className="mb-6 text-sm text-gray-500">
                {t("bottomNav.modal.description")}
              </p>

              {expandedOption === null && (
                <div className="mb-5 text-left">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Search by Mobile Number
                  </label>

                  <input
                    type="tel"
                    value={searchMobile}
                    onChange={(event) =>
                      setSearchMobile(
                        event.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    placeholder="Enter mobile number"
                    className="w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-transparent focus:ring-2 focus:ring-purple-500"
                    maxLength={10}
                  />

                  {searchMobileError && (
                    <p className="mt-2 text-xs text-red-600">
                      {searchMobileError}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleSearchMobile}
                    disabled={searchMobileLoading}
                    className={`mt-3 w-full rounded-md py-2.5 font-medium transition ${
                      searchMobileLoading
                        ? "cursor-not-allowed bg-gray-300 text-gray-600"
                        : "bg-purple-600 text-white hover:bg-purple-700"
                    }`}
                  >
                    {searchMobileLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              )}

              <div className="space-y-3">
                {expandedOption === null && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        // setExpandedOption("own");
                        setAddMode(null);
                        closeAddProfileModal();
                        navigate("/admin/user/profile/basic/own");
                      }}
                      className="flex w-full items-center justify-center rounded-md bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 text-white"
                    >
                      <FaUser className="mr-2" />
                      {t("bottomNav.modal.own")}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        // setExpandedOption("mediator");
                        setAddMode(null);
                        closeAddProfileModal();
                        navigate("/admin/user/profile/basic/mediator");
                      }}
                      className="flex w-full items-center justify-center rounded-md bg-gradient-to-r from-green-600 to-teal-600 px-4 py-3 text-white"
                    >
                      <FaUsers className="mr-2" />
                      {t("bottomNav.modal.mediator")}
                    </button>
                  </>
                )}

                {expandedOption === "own" && (
                  <div className="mt-2 space-y-2">
                    {addMode === null && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedOption(null);
                            setAddMode(null);
                          }}
                          className="w-full text-sm text-purple-600 underline"
                        >
                          ← {t("Back")}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            closeAddProfileModal();
                            navigate("/admin/user/profile/basic/own");
                          }}
                          className="w-full rounded-md bg-purple-600 py-3 font-medium text-white transition hover:bg-purple-700"
                        >
                          Add Basic Details
                        </button>

                        {/* <button
                          type="button"
                          onClick={() => {
                            closeAddProfileModal();

                            navigate("/admin/user/profile/own");
                          }}
                          className="w-full rounded-md bg-red-100 py-3 text-gray-700"
                        >
                          Add Full Details (Recommended)
                        </button> */}
                      </>
                    )}

                    {addMode === "ai" && (
                      <>
                        <button
                          type="button"
                          onClick={() => setAddMode(null)}
                          className="w-full text-sm text-purple-600 underline"
                        >
                          ← {t("Back")}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            closeAddProfileModal();

                            navigate("/admin/user/profile/text", {
                              state: {
                                isMediatorProfile: false,
                              },
                            });
                          }}
                          className="w-full rounded-md bg-blue-600 py-3 font-medium text-white transition hover:bg-blue-700"
                        >
                          Paste Biodata Text
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setIsMediatorProfile(false);
                            setActiveProfileType("biodata-upload");
                            setIsAddProfileModalOpen(false);
                          }}
                          className="w-full rounded-md bg-green-600 py-3 font-medium text-white transition hover:bg-green-700"
                        >
                          Upload Biodata Image
                        </button>
                      </>
                    )}
                  </div>
                )}

                {expandedOption === "mediator" && (
                  <div className="mt-2 space-y-2">
                    {addMode === null && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedOption(null);
                            setAddMode(null);
                          }}
                          className="w-full text-sm text-purple-600 underline"
                        >
                          ← {t("Back")}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            closeAddProfileModal();

                            navigate("/admin/user/profile/basic/mediator");
                          }}
                          className="w-full rounded-md bg-purple-600 py-3 font-medium text-white transition hover:bg-purple-700"
                        >
                          Add Basic Details
                        </button>

                        {/* <button
                          type="button"
                          onClick={() => {
                            closeAddProfileModal();

                            navigate("/admin/user/profile/mediator");
                          }}
                          className="w-full rounded-md bg-red-200 py-3 font-medium text-gray-800 transition hover:bg-red-300"
                        >
                          Add Full Details (Recommended)
                        </button> */}
                      </>
                    )}

                    {addMode === "ai" && (
                      <>
                        <button
                          type="button"
                          onClick={() => setAddMode(null)}
                          className="w-full text-sm text-purple-600 underline"
                        >
                          ← {t("Back")}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            closeAddProfileModal();

                            navigate("/admin/user/profile/text", {
                              state: {
                                isMediatorProfile: true,
                              },
                            });
                          }}
                          className="w-full rounded-md bg-blue-600 py-3 font-medium text-white transition hover:bg-blue-700"
                        >
                          Paste Biodata Text
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setIsMediatorProfile(true);
                            setActiveProfileType("biodata-upload");
                            setIsAddProfileModalOpen(false);
                          }}
                          className="w-full rounded-md bg-green-600 py-3 font-medium text-white transition hover:bg-green-700"
                        >
                          Upload Biodata Image
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={closeAddProfileModal}
                className="mt-4 rounded-md bg-gray-300 px-4 py-2 text-gray-700 transition hover:bg-gray-400"
              >
                {t("bottomNav.modal.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeProfileType === "biodata-upload" && (
        <BiodataUploadModal
          onClose={() => {
            setActiveProfileType(null);
            setIsMediatorProfile(false);
          }}
          bureauId={bureauInfo.bureauId}
          isMediatorProfile={isMediatorProfile}
        />
      )}
    </div>
  );
};

export default Gnavbar;
